import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ImageTag from "../components/reuseable/imageTag";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { ResetPasswordWithOtpAction, clearPasswordOtpState, clearPendingEmailForPasswordOtp, clearPendingOtpForPasswordReset } from "../Store/Actions/AuthActions";
import { PasswordOtpActionConst } from "../Store/Constants/AuthConstants";

function NewPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    pendingEmailForPasswordOtp,
    pendingOtpForPasswordReset,
    passwordOtpResetLoading,
    passwordOtpResetError,
    passwordOtpResetSuccess,
  } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const hasHandledSuccess = useRef(false); // Track if we've already handled success

  useEffect(() => {
    if (!pendingEmailForPasswordOtp || !pendingOtpForPasswordReset) {
      navigate("/forget-password");
    }
  }, [pendingEmailForPasswordOtp, pendingOtpForPasswordReset, navigate]);

  // Clear password OTP reset states on mount to prevent stale toasts
  useEffect(() => {
    // Clear success/error states to prevent stale toasts
    dispatch({
      type: PasswordOtpActionConst.RESET_FAIL,
      result: null,
    });
    return () => {
      // Cleanup on unmount
      dispatch({
        type: PasswordOtpActionConst.RESET_FAIL,
        result: null,
      });
    };
  }, [dispatch]);

  useEffect(() => {
    if (!passwordOtpResetError) return;
    const message = Array.isArray(passwordOtpResetError.message)
      ? passwordOtpResetError.message.join(", ")
      : passwordOtpResetError.message || "Password reset failed. Try again.";
    toast.error(message);
  }, [passwordOtpResetError]);

  useEffect(() => {
    // Only handle success if we're actually on the change password page and haven't handled it yet
    // This prevents the effect from firing when component remounts or state persists
    if (!passwordOtpResetSuccess || location.pathname !== "/change-password" || hasHandledSuccess.current) return;
    
    // Mark as handled immediately to prevent duplicate calls
    hasHandledSuccess.current = true;
    
    const message =
      passwordOtpResetSuccess?.message ||
      passwordOtpResetSuccess?.data?.message ||
      "Password reset successfully.";
    toast.success(message);
    setFormError("");
    setPassword("");
    setConfirmPassword("");
    // Clear state BEFORE navigation to prevent stale state
    dispatch(clearPasswordOtpState());
    dispatch(clearPendingEmailForPasswordOtp());
    dispatch(clearPendingOtpForPasswordReset());
    navigate("/");
  }, [passwordOtpResetSuccess, location.pathname, navigate, dispatch]);

  // Reset the ref when passwordOtpResetSuccess is cleared (allows handling success again if needed)
  useEffect(() => {
    if (!passwordOtpResetSuccess) {
      hasHandledSuccess.current = false;
    }
  }, [passwordOtpResetSuccess]);

  const validateForm = () => {
    if (!password || !confirmPassword) {
      setFormError("Both fields are required.");
      return false;
    }
    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return false;
    }
    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    if (!pendingEmailForPasswordOtp || !pendingOtpForPasswordReset) {
      navigate("/forget-password");
      return;
    }

    dispatch(
      ResetPasswordWithOtpAction({
        email: pendingEmailForPasswordOtp,
        otp: pendingOtpForPasswordReset,
        newPassword: password,
      })
    );
  };

  return (
    <div className="lg:h-screen flex items-center">
      <div className="flex w-full lg:h-screen rounded-xl">
        <div className="w-[60%] h-full hidden lg:flex lg:flex-col items-center justify-center bg-gradient-to-b from-[#3FC4E3] to-[#006077]">
          <ImageTag
            path="/assets/images/auth/thumbnail.png"
            classes="size-[460px]"
            altText="login"
          />
          <p className="font-popinsBold text-white text-4xl mt-8 text-center">
            One Platform.
          </p>
          <p className="font-popinsBold text-white text-4xl mt-2 text-center">
            Endless Community Possibilities.
          </p>
        </div>
        <div className=" w-full lg:w-[40%] h-screen lg:h-full my-auto flex flex-col items-center justify-center bg-bg-auth bg-no-repeat bg-center bg-cover">
          <div className="w-[90%] lg:w-[75%] mx-auto flex flex-col items-center justify-center">
            <div className="w-full pt-8 h-full flex flex-col justify-center text-center">
              <Link to={"/otp-password-reset"}>
                <div className="flex text-black/50 items-center gap-x-1 lg:ml-[-50px] font-popinsMedium">
                  <ChevronLeft size={18} />
                  <p>Back</p>
                </div>
              </Link>
              <h1 className="font-popinsBold  text-2xl 2xl:text-4xl mt-28">
                Create New Password
              </h1>
              <p className="text-textColor mb-4 w-[90%] mx-auto mt-2">
                Set a strong and secure password for your account. Make sure
                it’s something you can remember but difficult for others to
                guess.
              </p>
            </div>
            <form className="w-full mt-8" onSubmit={handleSubmit}>
              <div className="relative w-full mt-3">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full font-popinsMedium rounded-xl border-2 bg-white px-4 py-2 !text-black/50 outline-none transition-all duration-300 ease-in-out border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                />
                <div
                  title="Show password"
                  className="absolute right-3 top-3 cursor-pointer text-black/50"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </div>
              </div>
              <div className="relative w-full mt-3">
                <input
                  type={confirmShowPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full font-popinsMedium rounded-xl border-2 bg-white px-4 py-2 !text-black/50 outline-none transition-all duration-300 ease-in-out border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                />
                <div
                  title="Show password"
                  className="absolute right-3 top-3 cursor-pointer text-black/50"
                  onClick={() => setConfirmShowPassword((prev) => !prev)}
                >
                  {confirmShowPassword ? (
                    <Eye size={20} />
                  ) : (
                    <EyeOff size={20} />
                  )}
                </div>
              </div>
              {formError && (
                <p className="text-left text-sm text-red-500 mt-3 font-popinsMedium">
                  {formError}
                </p>
              )}
              <button
                type="submit"
                disabled={passwordOtpResetLoading}
                className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-lg mt-8 rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
           active:scale-95 active:shadow-inner focus:outline-none w-full
           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span className="flex items-center justify-center">
                  {passwordOtpResetLoading ? "Submitting..." : "Save Password"}
                </span>
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewPassword;

